import type { AxiosInstance } from 'axios';
import type { ContactResponse, FindOrCreateContactRequest } from './contracts/contact';
import type {
  ContactPhoneSearchResponse,
  GetContactPhonesParams,
  ContactCategoryResponse,
} from './contracts/contact-phone';

/**
 * Gateway for interacting with the Contact List API.
 */
export class ContactListGateway {
  constructor(
    private readonly httpClient: AxiosInstance,
    private readonly baseUrl: string,
  ) {}

  /**
   * Searches for contact phones with optional filtering and pagination.
   * @param params - The query parameters for the search.
   * @returns The paginated contact phones response.
   */
  async getContactsPhones(params: GetContactPhonesParams): Promise<ContactPhoneSearchResponse> {
    const { data } = await this.httpClient.get<ContactPhoneSearchResponse>(`${this.baseUrl}/contact/phone/search`, {
      params,
    });
    return data;
  }

  /**
   * Retrieves all contact categories.
   * @returns An array of contact categories.
   */
  async getCategoryList(): Promise<ContactCategoryResponse[]> {
    const { data } = await this.httpClient.get<ContactCategoryResponse[]>(`${this.baseUrl}/contact/category`);
    return data;
  }

  /**
   * Finds an existing contact or creates a new one.
   * @param payload - The contact data sent as query parameters and in the request body.
   * @returns The existing or newly created contact.
   */
  async findOrCreateContact(payload: FindOrCreateContactRequest): Promise<ContactResponse> {
    const { data } = await this.httpClient.put<ContactResponse>(`${this.baseUrl}/contact/find-or-create`, payload);
    return data;
  }
}

export type { ContactResponse, FindOrCreateContactRequest } from './contracts/contact';
export type {
  ContactPhoneSearchResponse,
  GetContactPhonesParams,
  ContactPhoneResponse,
  ContactCategoryResponse,
} from './contracts/contact-phone';
